import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'POSTPOP_COLOR_SCHEME_V2';
const COLOR_SCHEMES = ['light', 'dark', 'system'];
const THEME_COLORS = {
  light: '#ffffff',
  dark: '#11100f',
};

const ColorSchemeContext = createContext({
  colorScheme: 'system',
  resolvedMode: 'light',
  setColorScheme: () => {},
  toggleColorScheme: () => {},
});

const getSystemMode = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialScheme = () => {
  if (typeof window === 'undefined') {
    return 'system';
  }
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return COLOR_SCHEMES.includes(saved) ? saved : 'system';
};

export const ColorSchemeProvider = ({ children }) => {
  const [colorScheme, setColorSchemeState] = useState(getInitialScheme);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const resolvedMode = colorScheme === 'system' ? systemMode : colorScheme;

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;

    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute('content', THEME_COLORS[resolvedMode] || THEME_COLORS.light);
  }, [resolvedMode]);

  const setColorScheme = useCallback((nextScheme) => {
    const normalized = COLOR_SCHEMES.includes(nextScheme) ? nextScheme : 'system';
    setColorSchemeState(normalized);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    }
  }, []);

  const toggleColorScheme = useCallback(() => {
    // First visit follows the OS preference. A tap stores an explicit
    // light/dark preference and switches immediately.
    setColorScheme(resolvedMode === 'dark' ? 'light' : 'dark');
  }, [resolvedMode, setColorScheme]);

  const value = useMemo(
    () => ({
      colorScheme,
      resolvedMode,
      setColorScheme,
      toggleColorScheme,
    }),
    [colorScheme, resolvedMode, setColorScheme, toggleColorScheme],
  );

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
};

export const useColorScheme = () => useContext(ColorSchemeContext);
